import { orderBetween, type ListItem } from '@awwyeah/shared';
import type * as Y from 'yjs';
import { newId } from './utils/id';

/**
 * Keys inside a `list:<id>` doc, reconciled with the lists module (WS-5): the `items`
 * Y.Map holds the items, the `order` Y.Array tracks which ids exist, and each item's
 * fractional `order` FIELD (shared `orderBetween`) is the source of truth for display
 * order — appended items slot after the current last item.
 */
export const LIST_ITEMS_MAP_KEY = 'items';
export const LIST_ORDER_ARRAY_KEY = 'order';

export interface AddIngredientsResult {
  addedItemIds: string[];
  /** Removes exactly the items just added, nothing else — even if the list changed since. */
  undo: () => void;
}

/**
 * Appends ingredient lines to a shopping/todo list's Yjs doc (ROADMAP §2 Phase 1 Recipes,
 * item 5). Each ingredient's preserved `text` ("2 cups flour") becomes the item's text
 * verbatim — it's already the sensible display string the recipe stored.
 */
export function addIngredientsToList(
  listDoc: Y.Doc,
  ingredientTexts: string[],
  userId: string,
): AddIngredientsResult {
  const itemsMap = listDoc.getMap<ListItem>(LIST_ITEMS_MAP_KEY);
  const orderArray = listDoc.getArray<string>(LIST_ORDER_ARRAY_KEY);
  const now = new Date().toISOString();
  const addedItemIds: string[] = [];

  listDoc.transact(() => {
    let lastOrder =
      [...itemsMap.values()].reduce<number | null>(
        (max, it) =>
          typeof it.order === 'number' && (max === null || it.order > max) ? it.order : max,
        null,
      ) ?? null;
    for (const text of ingredientTexts) {
      const trimmed = text.trim();
      if (!trimmed) continue;
      const id = newId();
      const order = orderBetween(lastOrder, null);
      lastOrder = order;
      const item: ListItem = {
        id,
        text: trimmed,
        checked: false,
        assigneeIds: [],
        order,
        createdBy: userId,
        createdAt: now,
        updatedBy: userId,
        updatedAt: now,
      };
      itemsMap.set(id, item);
      orderArray.push([id]);
      addedItemIds.push(id);
    }
  });

  const undo = (): void => {
    listDoc.transact(() => {
      for (const id of addedItemIds) {
        itemsMap.delete(id);
        const currentOrder = orderArray.toArray();
        const index = currentOrder.indexOf(id);
        if (index >= 0) orderArray.delete(index, 1);
      }
    });
  };

  return { addedItemIds, undo };
}
